import type { CuadreUnit, RawAccount } from "./cuadre";
import type { SgPlatform } from "./platforms";
import type { RetransmissionsByCompany } from "./retransmissions";

// Vista Plataformas: por cada plataforma, una fila por cuenta (el «usuario» de
// Mapon, que es como ahí se identifica a la empresa) con sus unidades en esa
// plataforma partidas por facturación, reporte y etiqueta. Pura, para testearla.

export type PlatformTab = SgPlatform | "none";

export interface PlatformAccountRow {
  id: number; // 0 = unidades sin cuenta en SG
  name: string;
  company: string;
  billableAccount: boolean;
  accountCondition: string | null;
  // Integradores con retransmisión activa; null = sin dato (sólo v5 la tiene).
  retransmission: string[] | null;
  total: number;
  active: number;
  billable: number;
  notBillable: number;
  inactive: number;
  reporting: number; // reporte en las últimas 24 h
  silent: number;
  conditions: Record<string, number>; // etiqueta de equipo -> unidades
}

export const inPlatform = (u: CuadreUnit, p: PlatformTab) =>
  p === "none" ? u.platforms.length === 0 : u.platforms.includes(p);

export function buildPlatformRows(
  units: CuadreUnit[],
  accounts: RawAccount[],
  platform: PlatformTab,
  retrans?: RetransmissionsByCompany
): PlatformAccountRow[] {
  const byId = new Map(accounts.map((a) => [a.id, a]));
  const rows = new Map<number, PlatformAccountRow>();
  for (const u of units) {
    if (!inPlatform(u, platform)) continue;
    const id = u.accountId ?? 0;
    let r = rows.get(id);
    if (!r) {
      const a = byId.get(id);
      const v5Id = a?.v5CompanyId ?? null;
      r = {
        id,
        name: a?.name ?? "",
        company: a?.company ?? "",
        billableAccount: !!a?.billable,
        accountCondition: a?.condition ?? null,
        retransmission: platform === "v5" && retrans ? (v5Id ? (retrans.get(v5Id) ?? []) : []) : null,
        total: 0,
        active: 0,
        billable: 0,
        notBillable: 0,
        inactive: 0,
        reporting: 0,
        silent: 0,
        conditions: {},
      };
      rows.set(id, r);
    }
    r.total++;
    r[u.billing]++;
    if (u.billing !== "inactive") r.active++;
    if (u.report === "ok") r.reporting++;
    else r.silent++;
    if (u.condition) r.conditions[u.condition] = (r.conditions[u.condition] ?? 0) + 1;
  }
  return [...rows.values()];
}

// Etiquetas presentes, de la más usada a la menos: son columnas de la tabla y
// del Excel, así que sólo salen las que existen en la plataforma.
export function conditionKeys(rows: PlatformAccountRow[]): string[] {
  const totals: Record<string, number> = {};
  for (const r of rows) for (const [k, n] of Object.entries(r.conditions)) totals[k] = (totals[k] ?? 0) + n;
  return Object.keys(totals).sort((a, b) => totals[b] - totals[a] || a.localeCompare(b, "es"));
}

export function sumRows(rows: PlatformAccountRow[]) {
  const s = { accounts: rows.length, total: 0, active: 0, billable: 0, notBillable: 0, inactive: 0, reporting: 0, silent: 0, retransmitting: 0 };
  for (const r of rows) {
    s.total += r.total;
    s.active += r.active;
    s.billable += r.billable;
    s.notBillable += r.notBillable;
    s.inactive += r.inactive;
    s.reporting += r.reporting;
    s.silent += r.silent;
    if (r.retransmission?.length) s.retransmitting++;
  }
  return s;
}
